import { addDays, differenceInDays, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { DateRange, OnSelectHandler, PropsBase, PropsSingle, PropsRange } from 'react-day-picker';

import { IDaysRange } from 'detailsv2/structure';

import { DAY_OFFSET, INITIAL_DATE, MAX_DAYS } from './constants';
import { DaysRange, TProps } from './types';

const getInitialDate = () => startOfDay(parseISO(INITIAL_DATE));

export const parseDaysRange = (daysRange?: IDaysRange): DaysRange => ({
  from: daysRange?.start || undefined,
  to: daysRange?.end || undefined,
});

export const getDayNumberByDate = (date?: Date) => {
  const initialDate = getInitialDate();

  if (!date) return undefined;
  return differenceInDays(date, startOfDay(initialDate)) + DAY_OFFSET;
};

export const getDateByDayNumber = (dayNumber?: number) => {
  const initialDate = getInitialDate();

  if (!dayNumber) return undefined;
  return addDays(initialDate, dayNumber - DAY_OFFSET);
};

export const getPickerOptions = (
  dayParams: TProps,
): (PropsSingle | PropsRange) & Pick<PropsBase, 'startMonth' | 'endMonth' | 'defaultMonth'> => {
  const startMonth = startOfMonth(getInitialDate());
  const endMonth = addDays(startMonth, MAX_DAYS);

  if (dayParams.mode === 'single') {
    const defaultOptions = {
      defaultMonth: getDateByDayNumber(dayParams.selected) || dayParams.defaultMonth || startMonth,
      startMonth,
      endMonth,
    };

    const { selected, onSelect } = dayParams;

    const handleSelect: OnSelectHandler<Date | undefined> = (selectedDate, ...args) => {
      const dayNumber = getDayNumberByDate(selectedDate);
      onSelect && onSelect(dayNumber, ...args);
    };

    return {
      ...dayParams,
      ...defaultOptions,
      selected: getDateByDayNumber(selected),
      onSelect: handleSelect,
      required: false,
    };
  }

  if (dayParams.mode === 'range') {
    const parsedDaysRange = {
      start: getDateByDayNumber(dayParams.selected?.from),
      end: getDateByDayNumber(dayParams.selected?.to),
    };

    const defaultOptions = {
      defaultMonth: parsedDaysRange.end || parsedDaysRange.start || dayParams.defaultMonth || startMonth,
      startMonth,
      endMonth,
    };

    const { selected, onSelect } = dayParams;
    const handleSelect: OnSelectHandler<DateRange | undefined> = (dateRange, ...args) => {
      const dayRange = { from: getDayNumberByDate(dateRange?.from), to: getDayNumberByDate(dateRange?.to) };
      onSelect && onSelect(dayRange, ...args);
    };

    return {
      ...dayParams,
      ...defaultOptions,
      selected: { from: getDateByDayNumber(selected?.from), to: getDateByDayNumber(selected?.to) },
      onSelect: handleSelect,
      required: false,
    };
  }

  throw new Error('Unknown mode');
};
